import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Animations } from './components/Animations';
import { ApiProvider } from './components/ApiIntegration';
import { DesignSystem } from './components/DesignSystem';
import { GameAudioProvider } from './components/GameAudioProvider';
import GameStartScreen from './components/GameStartScreen';
import HowToPlayModal from './components/HowToPlayModal';
import LevelSelectionScreen from './components/LevelSelectionScreen';
import { StorageProvider, useStorage } from './components/LocalStorage';
import { AssessmentGame } from './components/AssessmentGame';
import { GAME_SCREENS, type GameScreen } from './components/constants';

function AppContent() {
  const [currentScreen, setCurrentScreen] = useState<GameScreen>(GAME_SCREENS.START);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);

  const storage = useStorage();
  const storageKey = storage.getGameSpecificStorageKey();

  const [userProgress, setUserProgress] = useState(() => {
    return storage.loadProgressSafely(storageKey, {
      unlockedLevel: 1,
      completedLevels: [],
      levelScores: {},
      levelStars: {},
      currentSet: 1
    });
  });

  const handleStartGame = () => {
    setCurrentScreen(GAME_SCREENS.LEVEL_SELECTION);
  };

  const handleShowHowToPlay = () => {
    setShowHowToPlay(true);
  };

  const handleCloseHowToPlay = () => {
    setShowHowToPlay(false);
  };

  const handleBack = (newScreen: GameScreen) => {
    setCurrentScreen(newScreen);
  };

  const handleLevelSelect = (level: number) => {
    setCurrentLevel(level);
    setCurrentScreen(GAME_SCREENS.GAMEPLAY);
  };

  const handleLevelComplete = (score: number, total: number, stars: number) => {
    const updatedProgress = {
      ...userProgress,
      completedLevels: userProgress.completedLevels.includes(currentLevel)
        ? userProgress.completedLevels
        : [...userProgress.completedLevels, currentLevel],
      levelScores: { ...userProgress.levelScores, [currentLevel]: score },
      levelStars: { ...userProgress.levelStars, [currentLevel]: stars },
      unlockedLevel: Math.max(userProgress.unlockedLevel, currentLevel + 1)
    };

    setUserProgress(updatedProgress);
    storage.saveProgressSafely(storageKey, updatedProgress);

    if (currentLevel < 2) {
      setCurrentLevel(currentLevel + 1);
      setCurrentScreen(GAME_SCREENS.GAMEPLAY);
    } else {
      setCurrentScreen(GAME_SCREENS.LEVEL_SELECTION);
    }
  };

  return (
    <DesignSystem>
      <Animations>
        <Toaster position="top-center" />

        {currentScreen === GAME_SCREENS.START && (
          <GameStartScreen
            gameTitle="Technical Assessment"
            gameDescription="Python Automation & Wi-Fi Testing - Professional Level Assessment"
            onStartGame={handleStartGame}
            onShowHowToPlay={handleShowHowToPlay}
          />
        )}

        {currentScreen === GAME_SCREENS.LEVEL_SELECTION && (
          <LevelSelectionScreen
            totalLevels={2}
            currentLevel={currentLevel}
            userProgress={userProgress}
            onLevelSelect={handleLevelSelect}
            gameTitle="Technical Assessment"
          />
        )}

        {currentScreen === GAME_SCREENS.GAMEPLAY && (
          <AssessmentGame
            currentLevel={currentLevel}
            onLevelComplete={handleLevelComplete}
          />
        )}

        <HowToPlayModal
          isOpen={showHowToPlay}
          onClose={handleCloseHowToPlay}
          gameTitle="Technical Assessment"
          instructions={[
            "This is a professional technical assessment with two levels covering Python automation and Wi-Fi testing.",
            "Each level contains 13 questions that must be answered sequentially.",
            "Questions include single-select, multi-select, and coding challenges.",
            "No immediate feedback is provided - you'll see results after completing all questions.",
            "For coding questions, write your complete solution in the provided code editor.",
            "Take your time and read each question carefully before answering.",
            "Your progress is automatically saved as you complete each level."
          ]}
        />
      </Animations>
    </DesignSystem>
  );
}

function App() {
  return (
    <StorageProvider>
      <ApiProvider>
        <GameAudioProvider>
          <AppContent />
        </GameAudioProvider>
      </ApiProvider>
    </StorageProvider>
  );
}

export default App;